// src/components/WorkoutEntryForm.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    Stack,
    NumberInput,
    NumberInputField,
    useToast,
} from "@chakra-ui/react";
import { useAuth } from '../../contexts/AuthContext';
import { SupabaseClient } from "../../supabaseClient";

export default function WorkoutEntryForm({ initialEntry = null, onSuccess }) {
    const { user } = useAuth();
    const isEditMode = Boolean(initialEntry);
    const toast = useToast();

    // Entry type: cardio, strength, rehab
    const [entryType, setEntryType] = useState(initialEntry?.logType === 'rehab' ? 'rehab' : (initialEntry?.workout_type || 'cardio'));
    const [date, setDate] = useState(
        initialEntry?.workout_date || new Date().toISOString().split("T")[0]
    );
    const [duration, setDuration] = useState(initialEntry?.duration_minutes || "");
    const [notes, setNotes] = useState(initialEntry?.notes || "");

    // Cardio fields
    const [activityType, setActivityType] = useState(
        initialEntry?.cardio_exercises?.[0]?.activity_type || 'Run'
    );
    const [distance, setDistance] = useState(
        initialEntry?.cardio_exercises?.[0]?.distance_km || ""
    );
    const [pace, setPace] = useState(
        initialEntry?.cardio_exercises?.[0]?.avg_pace_min_per_km || ""
    );

    // Strength fields
    const [exerciseName, setExerciseName] = useState(
        initialEntry?.strength_exercises?.[0]?.exercise_name || ""
    );
    const [weight, setWeight] = useState(
        initialEntry?.strength_exercises?.[0]?.weight_kg || ""
    );
    const [reps, setReps] = useState(
        initialEntry?.strength_exercises?.[0]?.reps || ""
    );

    // Rehab fields
    const [areaTargeted, setAreaTargeted] = useState(
        initialEntry?.area_targeted || ""
    );
    const [painLevel, setPainLevel] = useState(
        initialEntry?.pain_level || ""
    );

    const handleSubmit = async () => {
        if (!user?.id) return;

        // Rehab entry
        if (entryType === 'rehab') {
            const payload = {
                profile_id: user.id,
                workout_date: date,
                duration_minutes: duration ? parseInt(duration) : null,
                area_targeted: areaTargeted,
                pain_level: painLevel ? parseInt(painLevel) : null,
                notes,
            };

            if (isEditMode && initialEntry.logType === 'rehab') {
                const { error } = await SupabaseClient
                    .from('rehab_workouts')
                    .update(payload)
                    .eq('id', initialEntry.id);

                if (error) {
                    toast({ title: 'Failed to update rehab session', status: 'error' });
                    return;
                }
            } else {
                const { data, error } = await SupabaseClient
                    .from('rehab_workouts')
                    .insert(payload)
                    .single();

                if (error || !data) {
                    toast({ title: 'Failed to create rehab session', status: 'error' });
                    return;
                }
            }

            toast({ title: isEditMode ? 'Rehab updated!' : 'Rehab session saved!', status: 'success' });
            onSuccess?.();
            return;
        }

        // Workout entry (cardio or strength)
        const sessionPayload = {
            profile_id: user.id,
            workout_type: entryType,
            workout_date: date,
            duration_minutes: duration ? parseInt(duration) : null,
            notes,
        };

        let sessionId;
        if (isEditMode && initialEntry.logType === 'workout') {
            const { error } = await SupabaseClient
                .from('workout_sessions')
                .update(sessionPayload)
                .eq('id', initialEntry.id);

            if (error) {
                toast({ title: 'Failed to update session', status: 'error' });
                return;
            }
            sessionId = initialEntry.id;
            await SupabaseClient.from('strength_exercises').delete().eq('session_id', sessionId);
            await SupabaseClient.from('cardio_exercises').delete().eq('session_id', sessionId);
        } else {
            const { data, error } = await SupabaseClient
                .from('workout_sessions')
                .insert(sessionPayload)
                .select()
                .single();
            if (error || !data) {
                toast({ title: 'Failed to create session', status: 'error' });
                return;
            }
            sessionId = data.id;
        }

        // Add workout details
        if (entryType === 'cardio') {
            await SupabaseClient.from('cardio_exercises').insert({
                session_id: sessionId,
                activity_type: activityType,
                distance_km: distance,
                duration_minutes: duration,
                avg_pace_min_per_km: pace,
            });
        }

        if (entryType === 'strength') {
            await SupabaseClient.from('strength_exercises').insert({
                session_id: sessionId,
                exercise_name: exerciseName,
                set_number: 1,
                weight_kg: weight,
                reps,
            });
        }

        toast({ title: isEditMode ? 'Workout updated!' : 'Workout saved!', status: 'success' });
        onSuccess?.();
    };

    return (
        <Box>
            <Stack spacing={4}>
                <FormControl>
                    <FormLabel>Entry Type</FormLabel>
                    <Select value={entryType} onChange={(e) => setEntryType(e.target.value)}>
                        <option value="cardio">Cardio</option>
                        <option value="strength">Strength</option>
                        <option value="rehab">Rehab</option>
                    </Select>
                </FormControl>

                <FormControl>
                    <FormLabel>Date</FormLabel>
                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </FormControl>

                <FormControl>
                    <FormLabel>Duration (minutes)</FormLabel>
                    <NumberInput value={duration} onChange={(_, val) => setDuration(val)}>
                        <NumberInputField />
                    </NumberInput>
                </FormControl>

                {entryType === "cardio" && (
                    <>
                        <FormControl>
                            <FormLabel>Activity</FormLabel>
                            <Select value={activityType} onChange={(e) => setActivityType(e.target.value)}>
                                <option>Run</option>
                                <option>Bike</option>
                                <option>Swim</option>
                                <option>Walk</option>
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Distance (km)</FormLabel>
                            <Input
                                type="number"
                                value={distance}
                                onChange={(e) => setDistance(e.target.value)}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Average Pace (min/km)</FormLabel>
                            <Input
                                type="number"
                                value={pace}
                                onChange={(e) => setPace(e.target.value)}
                            />
                        </FormControl>
                    </>
                )}

                {entryType === "strength" && (
                    <>
                        <FormControl>
                            <FormLabel>Exercise Name</FormLabel>
                            <Input
                                value={exerciseName}
                                onChange={(e) => setExerciseName(e.target.value)}
                                placeholder="e.g. Bench Press"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Weight (kg)</FormLabel>
                            <Input
                                type="number"
                                value={weight}
                                onChange={(e) => setWeight(e.target.value)}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Reps</FormLabel>
                            <Input
                                type="number"
                                value={reps}
                                onChange={(e) => setReps(e.target.value)}
                            />
                        </FormControl>
                    </>
                )}

                {entryType === 'rehab' && (
                    <>
                        <FormControl>
                            <FormLabel>Area Targeted</FormLabel>
                            <Input
                                value={areaTargeted}
                                onChange={(e) => setAreaTargeted(e.target.value)}
                                placeholder="e.g. Hip, Shoulder"
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Pain Level (1–10)</FormLabel>
                            <NumberInput
                                min={1}
                                max={10}
                                value={painLevel}
                                onChange={(_, val) => setPainLevel(val)}
                            >
                                <NumberInputField />
                            </NumberInput>
                        </FormControl>
                    </>
                )}

                <FormControl>
                    <FormLabel>Notes</FormLabel>
                    <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional comments..."
                    />
                </FormControl>

                <Button onClick={handleSubmit} colorScheme="teal">
                    {isEditMode ? (entryType === 'rehab' ? 'Update Rehab' : 'Update Workout') : (entryType === 'rehab' ? 'Save Rehab' : 'Save Workout')}
                </Button>
            </Stack>
        </Box>
    );
}
